/**
 * useFileUrl Hook
 *
 * React hook for resolving file:// references to usable URLs.
 * Handles both stable file references and legacy direct URLs.
 * Local first: bytes this session still holds, then a copy in the user's auto-save folder
 * when the folder is set up and the browser has given access. When access is granted
 * later, views switch to the local copies without reloading anything from the cloud.
 *
 * The lookups themselves are shared (utils/displayUrl.ts): however many places show a file,
 * it is resolved once per session, all of them get the same address, and a view that mounts
 * again (a workspace opened a second time) shows it in its first render.
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { extractFileId, isFileReference, peekFileMeta, FileMetadata } from '../utils/fileRegistry';
import { getLocalFolderAccess, holdLocalUrl, isCurrentLocalUrl, LOCAL_FOLDER_ACCESS_EVENT } from '../utils/localSave';
import { isLocalMediaUrl } from '../utils/desktopFolder';
import { isLegacyMediaRef } from '../utils/mediaStorage';
import type { MediaLookups } from '../utils/mediaProgress';
import {
    findLocalDisplayUrl, forgetDisplayUrl, onLocalUrlFailed, peekDisplayUrl, referenceOfDisplayUrl, reportFailedUrl, resolveDisplayUrl,
    type DisplayMedia, type DisplayOptions,
} from '../utils/displayUrl';

export { resolveDisplayUrl, peekDisplayUrl, forgetDisplayUrl, referenceOfDisplayUrl };
export type { DisplayMedia, DisplayOptions };

// A local file can be moved or removed outside the app while the app shows it from memory of
// where it was. The picture, video or sound that then fails to load says so here, whichever
// component drew it, and the views showing that file look it up again (see the hooks below).
if (typeof window !== 'undefined') {
    window.addEventListener('error', (event) => {
        const element = event.target;
        if (element instanceof HTMLImageElement || element instanceof HTMLMediaElement) reportFailedUrl(element.currentSrc || element.src);
    }, true);
}

interface UseFileUrlResult {
    url: string | null;
    isLoading: boolean;
    error: string | null;
    metadata: FileMetadata | null;
    refresh: () => void;
    isLocal: boolean; // Indicates if the URL was resolved from local storage
}

type MediaRef = string | undefined | null;

interface Shown {
    url: string | null;
    isLoading: boolean;
    error: string | null;
    metadata: FileMetadata | null;
    isLocal: boolean;
}

/** The registry entry this session has read for a reference (the user's own, else the workspace owner's), if any. */
const metadataOf = (ref: MediaRef, userId: string | undefined, ownerUserId: string | undefined): FileMetadata | null => {
    const fileId = ref && isFileReference(ref) ? extractFileId(ref) : null;
    if (!fileId) return null;
    return peekFileMeta(userId, fileId) ?? (ownerUserId && ownerUserId !== userId ? peekFileMeta(ownerUserId, fileId) : null);
};

const shownFrom = (media: DisplayMedia, metadata: FileMetadata | null): Shown =>
    ({ url: media.url, isLoading: false, error: media.error, metadata, isLocal: media.isLocal });

const sameShown = (a: Shown, b: Shown): boolean =>
    a.url === b.url && a.isLoading === b.isLoading && a.error === b.error && a.metadata === b.metadata && a.isLocal === b.isLocal;

/**
 * Hook to resolve a file reference or URL to a usable URL
 * Tries this session's copy and the local auto-save folder first (when it is set up and the browser has given access)
 * Supports offline resolution using localFileMap when userId is not available
 *
 * @param userId - The user ID for file lookup (optional for offline mode)
 * @param fileRefOrUrl - A file:// reference or direct URL
 * @param workspaceName - Optional workspace name for local file resolution
 * @param ownerUserId - Optional project owner's userId for cross-user file:// resolution (shared projects)
 * @returns Object with resolved URL, loading state, and metadata
 */
/**
 * The owner of the open workspace (App), for every file shown inside it that does not name one:
 * in a workspace shared with this person, its files are the owner's, and only the owner's
 * registry knows them. Without it a picture shown through FileImage said "File not found".
 */
export const MediaOwnerContext = createContext<string | undefined>(undefined);

/**
 * The open canvas's count of lookups under way (utils/mediaProgress.ts), for the loading bar
 * in its top bar. Views outside the canvas are not counted.
 */
export const MediaLookupsContext = createContext<MediaLookups | null>(null);

export const useFileUrl = (
    userId: string | undefined,
    fileRefOrUrl: string | undefined | null,
    workspaceName?: string,
    ownerUserIdArg?: string
): UseFileUrlResult => {
    const contextOwner = useContext(MediaOwnerContext);
    const ownerUserId = ownerUserIdArg ?? contextOwner;
    // What is already known (nothing to resolve, or resolved earlier in this session) is shown in
    // the first render; only an unknown reference starts in the loading state
    const [shown, setShown] = useState<Shown>(() => {
        const known = peekDisplayUrl(fileRefOrUrl, { userId, workspaceName, ownerUserId });
        return known
            ? shownFrom(known, metadataOf(fileRefOrUrl, userId, ownerUserId))
            : { url: null, isLoading: true, error: null, metadata: null, isLocal: false };
    });
    // Counted for the canvas's loading bar while it looks
    const lookups = useContext(MediaLookupsContext);
    const looking = shown.isLoading && !!fileRefOrUrl;
    useEffect(() => {
        if (!lookups || !looking) return;
        return lookups.start();
    }, [lookups, looking]);
    // Each resolution is numbered and only the latest may set state, so a slow cloud lookup
    // that finishes after a newer (local) answer cannot replace it.
    const runRef = useRef(0);
    const show = useCallback((next: Shown) => setShown(prev => (sameShown(prev, next) ? prev : next)), []);

    const resolve = useCallback(async (afresh: boolean = false) => {
        const run = ++runRef.current;
        const options: DisplayOptions = { userId, workspaceName, ownerUserId };
        if (afresh) forgetDisplayUrl(fileRefOrUrl, userId);

        // Answered at once: nothing to show, a retired reference, a direct URL, bytes this session
        // still holds, or an earlier answer (an earlier run may still be loading; this one answers now)
        const known = peekDisplayUrl(fileRefOrUrl, options);
        if (known) {
            show(shownFrom(known, metadataOf(fileRefOrUrl, userId, ownerUserId)));
            return;
        }

        setShown(prev => (prev.isLoading && !prev.error ? prev : { ...prev, isLoading: true, error: null }));
        const media = await resolveDisplayUrl(fileRefOrUrl, options);
        if (run !== runRef.current) return;
        show(shownFrom(media, metadataOf(fileRefOrUrl, userId, ownerUserId)));
    }, [userId, fileRefOrUrl, workspaceName, ownerUserId, show]);

    useEffect(() => {
        void resolve();
    }, [resolve]);

    // A local address belongs to the session's cache, which releases what no view shows: say this one shows it
    useEffect(() => holdLocalUrl(shown.isLocal ? shown.url : null), [shown.url, shown.isLocal]);

    // When the local folder becomes readable, switch to the local copy if there is one. Nothing is
    // shown loading and nothing is asked of the cloud; without a local copy the URL stays as it is.
    // A local copy already shown stays, except an address the desktop app streams from: that one
    // reads the folder as it is now, so once the folder changed it is looked up again.
    useEffect(() => {
        if (!fileRefOrUrl || !isFileReference(fileRefOrUrl) || !workspaceName) return;
        const ref = fileRefOrUrl;
        const streamed = shown.isLocal && isLocalMediaUrl(shown.url) ? shown.url : null;
        if (shown.isLocal && !streamed) return;
        const onAccess = async () => {
            const run = runRef.current;
            const options: DisplayOptions = { userId, workspaceName, ownerUserId };
            if (streamed) {
                if (isCurrentLocalUrl(streamed)) return;
                const media = await resolveDisplayUrl(ref, options);
                if (run !== runRef.current) return;
                runRef.current++;
                show(shownFrom(media, metadataOf(ref, userId, ownerUserId)));
                return;
            }
            const local = await findLocalDisplayUrl(ref, options);
            // No local copy, or a newer resolution started meanwhile and decides
            if (!local || run !== runRef.current) return;
            runRef.current++; // a resolution still running must not replace the local copy
            show(shownFrom(local, metadataOf(ref, userId, ownerUserId)));
        };
        const listener = () => { void onAccess(); };
        window.addEventListener(LOCAL_FOLDER_ACCESS_EVENT, listener);
        return () => window.removeEventListener(LOCAL_FOLDER_ACCESS_EVENT, listener);
    }, [fileRefOrUrl, shown.isLocal, shown.url, workspaceName, userId, ownerUserId, show]);

    // The local address shown failed to load (the file was moved or removed outside the app): the
    // file is looked up again, in the folder and then the cloud. Once per reference: a file that
    // is there but cannot be shown would otherwise be looked up for ever.
    const retriedRef = useRef<MediaRef>(null);
    useEffect(() => {
        if (!shown.isLocal || !shown.url) return;
        const url = shown.url;
        return onLocalUrlFailed((failed) => {
            if (failed !== url || retriedRef.current === fileRefOrUrl) return;
            retriedRef.current = fileRefOrUrl;
            void resolve(true);
        });
    }, [shown.url, shown.isLocal, fileRefOrUrl, resolve]);

    // Looks the reference up again from the start, forgetting what the session resolved for it
    const refresh = useCallback(() => { void resolve(true); }, [resolve]);

    return {
        url: shown.url,
        isLoading: shown.isLoading,
        error: shown.error,
        metadata: shown.metadata,
        refresh,
        isLocal: shown.isLocal,
    };
};

interface ShownList {
    urls: (string | null)[];
    errors: (string | null)[];
    /** Which of the urls are of a copy on this device. */
    locals: boolean[];
    isLoading: boolean;
}

const NO_REFS: MediaRef[] = [];

const listFrom = (media: DisplayMedia[]): ShownList => ({
    urls: media.map(item => item.url),
    errors: media.map(item => (item.reason === 'signed-out' ? 'Sign in for cloud access' : item.error)),
    locals: media.map(item => item.isLocal),
    isLoading: false,
});

const sameItems = <T,>(a: T[], b: T[]): boolean => a.length === b.length && a.every((item, index) => item === b[index]);

/** The same lists as before wherever nothing changed, so what depends on them is not redone. */
const keepSame = (prev: ShownList, next: ShownList): ShownList => {
    const urls = sameItems(prev.urls, next.urls) ? prev.urls : next.urls;
    const errors = sameItems(prev.errors, next.errors) ? prev.errors : next.errors;
    const locals = sameItems(prev.locals, next.locals) ? prev.locals : next.locals;
    return urls === prev.urls && errors === prev.errors && locals === prev.locals && prev.isLoading === next.isLoading
        ? prev
        : { urls, errors, locals, isLoading: next.isLoading };
};

/**
 * Hook to resolve multiple file references at once
 * Useful for arrays of images (e.g., generatedImages, referenceImages)
 * Tries the local auto-save folder first when it is set up and the browser has given access
 */
export const useFileUrls = (
    userId: string | undefined,
    fileRefsOrUrls: (string | undefined | null)[],
    workspaceName?: string,
    ownerUserIdArg?: string
): {
    urls: (string | null)[];
    isLoading: boolean;
    errors: (string | null)[];
} => {
    const contextOwner = useContext(MediaOwnerContext);
    const ownerUserId = ownerUserIdArg ?? contextOwner;
    // Callers build the array anew on every render: the list is taken as changed only when an
    // entry is, so the lookups below run when the references change, not on every render
    const refsRef = useRef<MediaRef[]>(fileRefsOrUrls || NO_REFS);
    if (!sameItems(refsRef.current, fileRefsOrUrls || NO_REFS)) refsRef.current = fileRefsOrUrls || NO_REFS;
    const refs = refsRef.current;
    const hasRefsToResolve = refs.some(ref => !!ref && (isFileReference(ref) || isLegacyMediaRef(ref)));

    // When every entry is already known the lists are there in the first render; otherwise they
    // start empty and loading, as they always did
    const [shown, setShown] = useState<ShownList>(() => {
        const known = refs.map(ref => peekDisplayUrl(ref, { userId, workspaceName, ownerUserId }));
        return known.every(Boolean) ? listFrom(known as DisplayMedia[]) : { urls: [], errors: [], locals: [], isLoading: true };
    });
    const shownRef = useRef(shown);
    shownRef.current = shown;
    // Each stored file counts for the canvas's loading bar while the list looks
    const lookups = useContext(MediaLookupsContext);
    const lookingFor = shown.isLoading ? refs.filter(ref => !!ref && (isFileReference(ref) || isLegacyMediaRef(ref))).length : 0;
    useEffect(() => {
        if (!lookups || !lookingFor) return;
        return lookups.start(lookingFor);
    }, [lookups, lookingFor]);
    // Bumped to resolve again: access was granted while a resolution was running, or an address shown failed to load
    const [folderAccessVersion, setFolderAccessVersion] = useState(0);
    const runRef = useRef(0);
    const resolvingRef = useRef(false);

    useEffect(() => {
        const run = ++runRef.current;
        const options: DisplayOptions = { userId, workspaceName, ownerUserId };
        const known = refs.map(ref => peekDisplayUrl(ref, options));
        if (known.every(Boolean)) {
            resolvingRef.current = false;
            const next = listFrom(known as DisplayMedia[]); // an earlier run may still be loading
            setShown(prev => keepSame(prev, next));
            return;
        }

        resolvingRef.current = true;
        setShown(prev => (prev.isLoading ? prev : { ...prev, isLoading: true }));
        void Promise.all(refs.map((ref, index) => known[index] ?? resolveDisplayUrl(ref, options))).then((media) => {
            if (run !== runRef.current) return;
            resolvingRef.current = false;
            const next = listFrom(media);
            setShown(prev => keepSame(prev, next));
        });
    }, [userId, refs, workspaceName, ownerUserId, folderAccessVersion]);

    // Local addresses belong to the session's cache, which releases what no view shows: say this one shows them
    useEffect(() => {
        const releases = shown.urls.map((url, index) => holdLocalUrl(shown.locals[index] ? url : null));
        return () => releases.forEach(release => release());
    }, [shown.urls, shown.locals]);

    // When the local folder becomes readable, switch references that have a local copy over
    // to it, without showing anything loading and without refreshing cloud links.
    useEffect(() => {
        if (!hasRefsToResolve || !workspaceName) return;
        const onAccess = async () => {
            // Addresses the desktop app streams from read the folder as it is now: once the folder
            // changed they are looked up again, without anything being shown loading
            const before = shownRef.current;
            if (!resolvingRef.current && before.urls.some((url, index) => before.locals[index] && isLocalMediaUrl(url) && !isCurrentLocalUrl(url as string))) {
                const started = runRef.current;
                const media = await Promise.all(refsRef.current.map(ref => resolveDisplayUrl(ref, { userId, workspaceName, ownerUserId })));
                if (started !== runRef.current || resolvingRef.current) return;
                runRef.current++;
                setShown(prev => keepSame(prev, { ...listFrom(media), isLoading: prev.isLoading }));
                return;
            }
            // Events also come when auto-save is switched off or another folder is picked: only a readable folder helps
            if ((await getLocalFolderAccess(userId).catch(() => null))?.state !== 'granted') return;
            if (resolvingRef.current) {
                setFolderAccessVersion(v => v + 1); // a resolution is running: run it again with the folder open
                return;
            }
            const run = runRef.current;
            const current = refsRef.current;
            const locals = shownRef.current.locals;
            // Only what is not shown from this device yet is looked for
            const found = await Promise.all(current.map((ref, index) => (
                !ref || !isFileReference(ref) || locals[index] ? null : findLocalDisplayUrl(ref, { userId, workspaceName, ownerUserId })
            )));
            if (run !== runRef.current || resolvingRef.current) return;
            if (!found.some(Boolean)) return;
            runRef.current++;
            setShown(prev => keepSame(prev, {
                urls: prev.urls.map((url, index) => found[index]?.url ?? url),
                errors: prev.errors.map((err, index) => (found[index] ? null : err)),
                locals: prev.locals.map((local, index) => (found[index] ? true : local)),
                isLoading: prev.isLoading,
            }));
        };
        const listener = () => { void onAccess(); };
        window.addEventListener(LOCAL_FOLDER_ACCESS_EVENT, listener);
        return () => window.removeEventListener(LOCAL_FOLDER_ACCESS_EVENT, listener);
    }, [hasRefsToResolve, workspaceName, userId, ownerUserId]);

    // A local address shown failed to load (the file was moved or removed outside the app): that
    // entry is looked up again, in the folder and then the cloud, once per reference
    const retriedRef = useRef(new Set<string>());
    const showsLocal = shown.locals.some(Boolean);
    useEffect(() => {
        if (!showsLocal) return;
        return onLocalUrlFailed((failed) => {
            const current = refsRef.current;
            const now = shownRef.current;
            let again = false;
            now.urls.forEach((url, index) => {
                const ref = current[index];
                if (url !== failed || !now.locals[index] || !ref || retriedRef.current.has(ref)) return;
                retriedRef.current.add(ref);
                forgetDisplayUrl(ref, userId);
                again = true;
            });
            if (again) setFolderAccessVersion(v => v + 1);
        });
    }, [showsLocal, userId]);

    return { urls: shown.urls, isLoading: shown.isLoading, errors: shown.errors };
};

/**
 * Synchronous check if a value is a file reference
 * Useful for conditional rendering without async
 */
export const checkIsFileReference = isFileReference;

export default useFileUrl;
